import { useCallback, useEffect, useRef } from 'react';

import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { EditorInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import { sendToParentProductFromEmbeddedConfluence } from '@atlassian/embedded-confluence-common/send-to-parent-product-from-embedded-confluence';
import {
	MAX_SELECTED_TEXT_LENGTH,
	type SendToParentProductPayload,
} from '@atlassian/embedded-confluence-common/iframe/sendToParentProduct';

import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset.ts';
import { fg } from '@confluence/feature-gating';

const DEFAULT_SELECTION_SEND_DEBOUNCE_MS = 150;
const SIGNATURE_HASH_MODULUS = 2_147_483_647;

const getSourceUrl = (): string | undefined => {
	return typeof window !== 'undefined' ? window.location.href : undefined;
};

const createClearedPayload = (contentId: string): SendToParentProductPayload => ({
	eventType: 'embedded-confluence.text-selection.cleared',
	contentId,
});

export const createTextSelectionPayload = ({
	contentId,
	selectionMarkdown,
	selectionLocalIds,
	sourceTitle,
}: {
	contentId: string;
	selectionMarkdown: string | null | undefined;
	selectionLocalIds?: string[] | null;
	sourceTitle?: string | null;
}): SendToParentProductPayload => {
	const selectedText = selectionMarkdown?.trim().slice(0, MAX_SELECTED_TEXT_LENGTH);
	if (!selectedText) {
		return createClearedPayload(contentId);
	}

	const sourceUrl = getSourceUrl();

	const payload: SendToParentProductPayload = {
		eventType: 'embedded-confluence.text-selection.changed',
		contentId,
		selectedText,
		...(selectionLocalIds?.length ? { selectionLocalIds } : {}),
		...(sourceTitle ? { sourceTitle } : {}),
		...(sourceUrl ? { sourceUrl } : {}),
	};

	return payload;
};

export const useEmbeddedConfluenceTextSelectionBridge = ({
	contentId,
	enabled,
	fullPageEditorPresetAPI,
	isEmbeddedConfluence,
	sourceTitle,
	debounceMs = DEFAULT_SELECTION_SEND_DEBOUNCE_MS,
}: {
	contentId: string;
	enabled: boolean;
	fullPageEditorPresetAPI: FullPageEditorPresetAPI | undefined;
	isEmbeddedConfluence: boolean;
	sourceTitle?: string | null;
	debounceMs?: number;
}): void => {
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pendingPayloadKeyRef = useRef<string | null>(null);
	const lastSentPayloadKeyRef = useRef<string | null>(null);
	const isBridgeEnabled =
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		enabled && isEmbeddedConfluence && fg('embedded_confluence_parent_product_sender');

	const clearPendingSend = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
		pendingPayloadKeyRef.current = null;
	}, []);

	const createPayloadSignature = useCallback((payload: SendToParentProductPayload): string => {
		if (payload.eventType === 'embedded-confluence.text-selection.cleared') {
			return `${payload.eventType}:${payload.contentId}`;
		}

		return [
			payload.eventType,
			payload.contentId,
			payload.contentType ?? '',
			createStringSignature(payload.selectedText),
			createStringSignature(payload.sourceTitle),
			createStringSignature(payload.sourceUrl),
			payload.selectionLocalIds?.join('\u001f') ?? '',
		].join('\u001e');
	}, []);

	const scheduleSend = useCallback(
		(payload: SendToParentProductPayload) => {
			const payloadKey = createPayloadSignature(payload);
			if (
				payloadKey === lastSentPayloadKeyRef.current ||
				payloadKey === pendingPayloadKeyRef.current
			) {
				return;
			}

			clearPendingSend();
			pendingPayloadKeyRef.current = payloadKey;
			timerRef.current = setTimeout(() => {
				timerRef.current = null;
				pendingPayloadKeyRef.current = null;
				void sendToParentProductFromEmbeddedConfluence(payload)
					.then((wasSent) => {
						if (wasSent) {
							lastSentPayloadKeyRef.current = payloadKey;
						}
					})
					.catch(() => undefined);
			}, debounceMs);
		},
		[clearPendingSend, createPayloadSignature, debounceMs],
	);

	usePluginStateEffect(
		fullPageEditorPresetAPI as EditorInjectionAPI<any, any> | undefined,
		['aiExperience'],
		({ aiExperienceState }) => {
			const payload = createTextSelectionPayload({
				contentId,
				selectionMarkdown: aiExperienceState?.selectionContext?.selectionMarkdown,
				selectionLocalIds: aiExperienceState?.selectionContext?.localIds,
				sourceTitle,
			});

			if (
				payload.eventType === 'embedded-confluence.text-selection.cleared' &&
				lastSentPayloadKeyRef.current === null
			) {
				clearPendingSend();
				return;
			}

			scheduleSend(payload);
		},
		{ disabled: !isBridgeEnabled },
	);

	useEffect(() => {
		if (!isBridgeEnabled) {
			clearPendingSend();
			lastSentPayloadKeyRef.current = null;
			return;
		}

		return () => {
			clearPendingSend();

			if (lastSentPayloadKeyRef.current !== null) {
				const payload = createClearedPayload(contentId);
				void sendToParentProductFromEmbeddedConfluence(payload);
			}
			lastSentPayloadKeyRef.current = null;
		};
	}, [clearPendingSend, contentId, isBridgeEnabled]);
};

const createStringSignature = (value: string | undefined): string => {
	if (!value) {
		return '';
	}

	let hash = 0;
	for (let index = 0; index < value.length; index += 1) {
		hash = (hash * 31 + value.charCodeAt(index)) % SIGNATURE_HASH_MODULUS;
	}

	return `${value.length}:${hash.toString(36)}`;
};
